import { useState } from 'react';
import { X, GripVertical, Save, Loader2 } from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { subsetsApi } from '../../api/subsets';
import type { Subset } from '../../types';

interface SubsetOrderModalProps {
  subsets: Subset[];
  onClose: () => void;
  onSave: () => void;
}

interface SortableSubsetItemProps {
  subset: Subset;
  index: number;
}

function SortableSubsetItem({ subset, index }: SortableSubsetItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subset.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-monster-dark rounded border border-monster-border"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white transition-colors"
      >
        <GripVertical size={20} />
      </button>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm font-mono w-6">#{index + 1}</span>
          <h3 className="font-semibold">{subset.name}</h3>
        </div>
        <p className="text-xs text-gray-400">
          {subset.songCount || 0} song{subset.songCount !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
}

export function SubsetOrderModal({ subsets, onClose, onSave }: SubsetOrderModalProps) {
  const [orderedSubsets, setOrderedSubsets] = useState<Subset[]>([...subsets]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedSubsets((items) => {
        const oldIndex = items.findIndex(s => s.id === active.id);
        const newIndex = items.findIndex(s => s.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Update each subset with its new display_order
      await Promise.all(
        orderedSubsets.map((subset, index) =>
          subsetsApi.updateSubset(subset.id, { displayOrder: index })
        )
      );

      onSave(); // Trigger parent refresh
      onClose(); // Close modal
    } catch (err) {
      console.error('Failed to update subset order:', err);
      setError('Failed to save new order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[90vh] bg-monster-card border border-monster-border rounded-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-monster-border">
          <div>
            <h2 className="text-xl font-bold">Reorder Subsets</h2>
            <p className="text-sm text-gray-400 mt-1">
              Drag to change the order subsets appear in tabs
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
            aria-label="Close"
          >
            <X size={28} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-600/20 border border-red-600/50 rounded text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
            <SortableContext items={orderedSubsets.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {orderedSubsets.map((subset, index) => (
                  <SortableSubsetItem key={subset.id} subset={subset} index={index} />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {orderedSubsets.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              No subsets to reorder
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-monster-border">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded hover:bg-monster-dark transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 rounded bg-monster-blue text-white hover:bg-monster-blue/80 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Order
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
